import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SubscriptionStatus } from '../../../../services/paymentService';
import { UnifiedPlan } from '../../types';
import { TIER_LABELS } from '../../types';
import { UpgradeProration } from '../../hooks/useSubscriptionData';

interface UpgradeProrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  upgradeProration: UpgradeProration | null;
  subscriptionStatus: SubscriptionStatus | null;
  pendingUpgradePlanId: string | null;
  unifiedPlans: UnifiedPlan[];
  prorationWizardStep: number;
  setProrationWizardStep: (step: number) => void;
  upgradeLoading: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function UpgradeProrationModal({
  open,
  onOpenChange,
  upgradeProration,
  subscriptionStatus,
  pendingUpgradePlanId,
  unifiedPlans,
  prorationWizardStep,
  setProrationWizardStep,
  upgradeLoading,
  onConfirm,
  onCancel,
}: UpgradeProrationModalProps) {
  return (
    <Dialog 
      open={open} 
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl">Confirm plan changes</DialogTitle>
          <DialogDescription>
            Review your subscription upgrade details and proration calculation before proceeding.
          </DialogDescription>
        </DialogHeader>
        {upgradeProration && subscriptionStatus ? (
          <>
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mt-4 mb-6">
              <div className={`flex items-center gap-2 ${prorationWizardStep >= 1 ? 'text-foreground' : 'text-muted-foreground'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${prorationWizardStep >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  {prorationWizardStep > 1 ? '✓' : '1'}
                </div>
                <span className="text-sm font-medium">Information</span>
              </div>
              <div className="w-8 h-px bg-border" />
              <div className={`flex items-center gap-2 ${prorationWizardStep >= 2 ? 'text-foreground' : 'text-muted-foreground'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${prorationWizardStep >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  2
                </div>
                <span className="text-sm font-medium">Calculation</span>
              </div>
            </div>

            {/* Step 1: Important Information */}
            {prorationWizardStep === 1 && (
              <div className="space-y-4 mt-4">
                <div className="text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    You are upgrading from <strong className="text-foreground">{TIER_LABELS[upgradeProration.old_tier] || upgradeProration.old_tier}</strong> to <strong className="text-foreground">{TIER_LABELS[upgradeProration.new_tier] || upgradeProration.new_tier}</strong>.
                  </p>
                  {(() => {
                    // Find the actual plan to get the real price
                    const targetPlan = unifiedPlans
                      .flatMap(up => [
                        ...Object.values(up.monthly_plans || {}),
                        ...Object.values(up.annual_plans || {})
                      ])
                      .find(p => p.paypal_plan_id === pendingUpgradePlanId);
                    
                    // Get actual plan price (discounted_price if available, otherwise base_price)
                    const actualPlanPrice = targetPlan 
                      ? (targetPlan.discounted_price ?? targetPlan.base_price ?? 0)
                      : parseFloat(upgradeProration.new_price || '0');
                    
                    // Use total_due_today from backend response (already calculated correctly)
                    // Fallback to prorated_amount if total_due_today is not provided
                    const totalDueToday = upgradeProration.total_due_today 
                      ? parseFloat(upgradeProration.total_due_today)
                      : parseFloat(upgradeProration.prorated_amount || '0');
                    
                    // Check if free upgrade is enabled
                    const isFreeUpgrade = upgradeProration.is_free_upgrade === true;
                    // If free upgrade, total due today should be 0 (backend already handles this)
                    const finalTotalDueToday = isFreeUpgrade ? 0 : totalDueToday;
                    
                    const nextCycle = upgradeProration.billing_cycle_end_date
                      ? new Date(upgradeProration.billing_cycle_end_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : null;
                    
                    return (
                      <p className="text-sm text-muted-foreground">
                        {isFreeUpgrade ? (
                          <>
                            You will be charged <strong className="text-foreground">$0.00</strong> {upgradeProration.currency || 'USD'} today for this free upgrade. Starting{nextCycle ? ` ${nextCycle}` : ''}, your subscription renews at <strong className="text-foreground">${actualPlanPrice.toFixed(2)}</strong> {upgradeProration.new_billing_cycle === 'annual' ? 'per year' : 'per month'}.
                          </>
                        ) : (
                          <>
                            You will be charged <strong className="text-foreground">${finalTotalDueToday.toFixed(2)}</strong> {upgradeProration.currency || 'USD'} today for the remaining time in this cycle. Starting{nextCycle ? ` ${nextCycle}` : ''}, your subscription renews at <strong className="text-foreground">${actualPlanPrice.toFixed(2)}</strong> {upgradeProration.new_billing_cycle === 'annual' ? 'per year' : 'per month'}.
                          </>
                        )}
                      </p>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Step 2: Invoice-style breakdown */}
            {prorationWizardStep === 2 && (
              <div className="space-y-4 mt-4">
                {(() => {
                  // Find the actual plan to get the real price
                  const targetPlan = unifiedPlans
                    .flatMap(up => [
                      ...Object.values(up.monthly_plans || {}),
                      ...Object.values(up.annual_plans || {})
                    ])
                    .find(p => p.paypal_plan_id === pendingUpgradePlanId);
                  
                  // Get actual plan price (discounted_price if available, otherwise base_price)
                  const actualPlanPrice = targetPlan 
                    ? (targetPlan.discounted_price ?? targetPlan.base_price ?? 0)
                    : parseFloat(upgradeProration.new_price || '0');
                  
                  // NEW: Use full price method for display
                  const fullNewPlanPrice = upgradeProration.full_new_plan_price 
                    ? parseFloat(upgradeProration.full_new_plan_price)
                    : parseFloat(upgradeProration.proration_price || upgradeProration.new_price || '0');
                  const alreadyPaid = parseFloat(upgradeProration.already_paid || upgradeProration.old_price || '0');
                  const remainingDaysProrate = parseFloat(upgradeProration.remaining_days_prorate || '0');
                  const netCredit = parseFloat(upgradeProration.net_credit || '0');
                  
                  // Use total_due_today from backend (calculated as full_new_plan_price - net_credit)
                  const totalDueToday = upgradeProration.total_due_today 
                    ? parseFloat(upgradeProration.total_due_today)
                    : parseFloat(upgradeProration.prorated_amount || '0');
                  
                  // Check if free upgrade is enabled
                  const isFreeUpgrade = upgradeProration.is_free_upgrade === true;
                  const freeUpgradePromotionAmount = upgradeProration.free_upgrade_promotion_amount 
                    ? parseFloat(upgradeProration.free_upgrade_promotion_amount) 
                    : 0;
                  const finalTotalDueToday = isFreeUpgrade ? 0 : totalDueToday;
                  
                  // Get next billing date for display
                  const nextBillingDate = upgradeProration.billing_cycle_end_date
                    ? new Date(upgradeProration.billing_cycle_end_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : null;
                  
                  return (
                    <>
                      {/* Pro Subscription - Full Price */}
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-medium text-foreground">
                            {TIER_LABELS[upgradeProration.new_tier] || upgradeProration.new_tier} subscription
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {isFreeUpgrade
                              ? "No immediate charge for this free upgrade."
                              : `Billed ${upgradeProration.new_billing_cycle === 'annual' ? 'annually' : 'monthly'}, starting today`
                            }
                          </div>
                        </div>
                        <div className="text-right font-medium text-foreground">
                          ${fullNewPlanPrice.toFixed(2)}
                        </div>
                      </div>

                      {/* Adjustment Section */}
                      {netCredit > 0 && (
                        <div className="pt-2 border-t border-border">
                          <div className="text-sm font-medium text-foreground mb-2">Adjustment</div>
                          
                          {/* Already Paid */}
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex-1 text-sm text-muted-foreground">
                              Already paid for current plan
                            </div>
                            <div className="text-right text-sm text-muted-foreground">
                              ${alreadyPaid.toFixed(2)}
                            </div>
                          </div>
                          
                          {/* Current Plan Spending Adjustment */}
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex-1 text-sm text-muted-foreground">
                              Current plan spending adjustment ({upgradeProration.days_used || 0} day{upgradeProration.days_used !== 1 ? 's' : ''} used)
                            </div>
                            <div className="text-right text-sm text-muted-foreground">
                              +${remainingDaysProrate.toFixed(2)}
                            </div>
                          </div>
                          
                          {/* Net Credit */}
                          <div className="flex justify-between items-start pt-1 border-t border-border mt-1">
                            <div className="flex-1 text-sm font-medium text-foreground">
                              Net credit
                            </div>
                            <div className="text-right font-medium text-green-600 dark:text-green-400">
                              -${netCredit.toFixed(2)}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Free Upgrade Promotion Line Item */}
                      {isFreeUpgrade && freeUpgradePromotionAmount > 0 && (
                        <div className="flex justify-between items-start pt-2 border-t border-border">
                          <div className="flex-1">
                            <div className="font-medium text-foreground">Free Upgrade Promotion</div>
                            <div className="text-sm text-muted-foreground mt-1">
                              Promotion applied - upgrade is free!
                            </div>
                          </div>
                          <div className="text-right font-medium text-green-600 dark:text-green-400">
                            -${freeUpgradePromotionAmount.toFixed(2)}
                          </div>
                        </div>
                      )}

                      {/* Renewal Information */}
                      {actualPlanPrice > 0 && (
                        <div className="text-sm text-muted-foreground pt-2 border-t border-border">
                          {nextBillingDate 
                            ? `Starting ${nextBillingDate}, your subscription renews at $${actualPlanPrice.toFixed(2)} ${upgradeProration.new_billing_cycle === 'annual' ? 'per year' : 'per month'}.`
                            : `Your subscription will renew at $${actualPlanPrice.toFixed(2)} ${upgradeProration.new_billing_cycle === 'annual' ? 'per year' : 'per month'}.`
                          }
                        </div>
                      )}

                      {/* Total Due Today */}
                      <div className="flex justify-between items-center pt-4 border-t-2 border-border">
                        <div className="font-semibold text-lg text-foreground">Total due today</div>
                        <div className="text-right">
                          <div className="font-semibold text-lg text-foreground">
                            ${finalTotalDueToday.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
          </>
        ) : (
          <div className="mt-4 text-center">
            <p className="text-sm text-muted-foreground">Loading upgrade details...</p>
          </div>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0 mt-6">
          <Button
            variant="outline"
            onClick={onCancel}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          {prorationWizardStep === 1 ? (
            <Button
              onClick={() => setProrationWizardStep(2)}
              className="w-full sm:w-auto"
            >
              View calculation details
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setProrationWizardStep(1)}
                className="w-full sm:w-auto"
              >
                Back
              </Button>
              <Button
                onClick={onConfirm}
                disabled={upgradeLoading !== null}
                className="w-full sm:w-auto"
              >
                {upgradeLoading ? 'Processing...' : 'Pay now'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

